// Minimal toast bus + a global error net. An exception in an event handler or an
// async callback otherwise surfaces *nothing* — this makes failures honest.

export interface Toast {
  id: number;
  msg: string;
  kind: 'info' | 'err';
}

let seq = 1;
let toasts: Toast[] = [];
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((l) => l());
}

export function subscribeToasts(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
export function getToasts(): Toast[] {
  return toasts;
}

export function toast(msg: string, kind: 'info' | 'err' = 'info', ttl = 4200): void {
  const t: Toast = { id: seq++, msg, kind };
  toasts = [...toasts, t];
  emit();
  setTimeout(() => {
    toasts = toasts.filter((x) => x.id !== t.id);
    emit();
  }, ttl);
}

let lastAt = 0;
function surface(kind: string, e: unknown): void {
  try {
    // eslint-disable-next-line no-console
    console.error('[' + kind + ']', e);
    const now = Date.now();
    if (now - lastAt < 2000) return; // throttle bursts
    lastAt = now;
    const err = e as { message?: string; reason?: { message?: string } } | string;
    const msg = typeof err === 'string' ? err : err?.message || err?.reason?.message || String(e);
    toast('Something went wrong — ' + msg + ' (see console)', 'err');
  } catch {
    /* the reporter must never throw */
  }
}

export function installErrorNet(): void {
  addEventListener('error', (e) => {
    if (e.error || e.message) surface('uncaught', e.error || e.message);
  });
  addEventListener('unhandledrejection', (e) => surface('rejection', e.reason || e));
}
